import { useEffect, useMemo, useRef, useState } from 'react';
import { AuthService } from '../services/authService';
import { Article } from '../types';

interface UseAuthorNamesOptions {
  enabled?: boolean;
  fallbackLabel?: string;
  onError?: (error: unknown) => void;
}

const useAuthorNames = (
  articles: Article[],
  options: UseAuthorNamesOptions = {}
) => {
  const { enabled = true, fallbackLabel, onError } = options;
  const [authorNames, setAuthorNames] = useState<Record<string, string>>({});
  const onErrorRef = useRef<UseAuthorNamesOptions['onError']>(onError);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const authorIds = useMemo(() => {
    const ids = articles.map(article => article.authorId).filter(Boolean);
    return Array.from(new Set(ids));
  }, [articles]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (authorIds.length === 0) {
      setAuthorNames({});
      return;
    }

    let isCancelled = false;

    const loadAuthorNames = async () => {
      try {
        const authors = await AuthService.getUsersByIds(authorIds);
        if (isCancelled) {
          return;
        }
        const authorNamesMap: Record<string, string> = {};
        authors.forEach(author => {
          authorNamesMap[author.id] = author.name;
        });
        setAuthorNames(authorNamesMap);
      } catch (error) {
        onErrorRef.current?.(error);
        if (fallbackLabel) {
          const fallbackMap: Record<string, string> = {};
          authorIds.forEach(id => {
            fallbackMap[id] = fallbackLabel;
          });
          setAuthorNames(fallbackMap);
        }
      }
    };

    loadAuthorNames();

    return () => {
      isCancelled = true;
    };
  }, [authorIds, enabled, fallbackLabel]);

  return authorNames;
};

export default useAuthorNames;
